import {
  UndefinedInitialDataOptions,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'

import { QUERY_KEY_COMMENT_LIST } from '@/features/comment/constants'
import {
  RequestMarkThreadActionable,
  RequestUpdateComment,
  ResponseCreateComment,
} from '@/schema'
import { SortOrder } from '@/types/common'
import { convertSortToApiFormat } from '@/utils/common'
import { DEFAULT_PAGE, TABLE_PAGE_SIZE } from '@/utils/constants'

import { ErrorType } from '../auth/axios-config'

import { annotationApi } from './services'
import { ThreadCommentFilters } from './types'
import { ANNOTATIONS_COMMENTS, ANNOTATIONS_THREAD_COMMENTS } from '.'

const DEFAULT_GET_ALL_LIMIT = 100
const COMMENTS_STALE_TIME = 30 * 1000 // 30 seconds
const COMMENTS_GC_TIME = 10 * 60 * 1000 // 10 minutes
const SYNC_REFETCH_TIME = 10 * 1000 // 10 seconds

export const useCreateComment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ResponseCreateComment) =>
      annotationApi.createComment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ANNOTATIONS_COMMENTS() })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}

export const useGetThreadsList = (params: {
  documentVersionId: string
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortOrder
  }
  filters?: ThreadCommentFilters
}) => {
  const {
    documentVersionId,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    filters,
  } = params
  const filterBy: Record<string, string[]> = {}
  const sortBy = convertSortToApiFormat(
    sort || { column: null, direction: null },
    '-created_at'
  )
  filterBy.document_version_id = [params.documentVersionId]

  return useInfiniteQuery({
    queryKey: ANNOTATIONS_THREAD_COMMENTS([
      'thread-list',
      documentVersionId,
      rowsPerPage,
      sortBy,
      JSON.stringify(filters || {}),
    ]),
    queryFn: ({ pageParam = DEFAULT_PAGE }) =>
      annotationApi.getThreadsList({
        filter_by: filterBy,
        sort_by: sortBy,
        limit: rowsPerPage,
        offset: pageParam * rowsPerPage,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const hasMore = lastPage?.pagination.has_next ?? false
      return hasMore ? allPages.length : undefined
    },
    initialPageParam: DEFAULT_PAGE,
    enabled: !!documentVersionId,
    staleTime: COMMENTS_STALE_TIME,
    gcTime: COMMENTS_GC_TIME,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  })
}

export const useDeleteComment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => annotationApi.deleteComment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ANNOTATIONS_COMMENTS() })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}

export const useUpdateComment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RequestUpdateComment }) =>
      annotationApi.updateComment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ANNOTATIONS_COMMENTS() })
      queryClient.invalidateQueries({ queryKey: ['tfl', 'comments'] })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}

/**
 * Hook to mark a thread as actionable
 * @returns React Query mutation hook
 */
export const useMarkThreadActionable = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: RequestMarkThreadActionable
    }) => annotationApi.markThreadActionable(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ANNOTATIONS_THREAD_COMMENTS([
          'thread-list',
          response.data[0].document_version_id,
        ]),
      })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}

/**
 * Hook to fetch a list of comments
 * @param params - Parameters for the query
 * @returns React Query infinite query hook
 */
export const useGetCommentsList = (params: {
  threadId: string
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortOrder
  }
  filters?: ThreadCommentFilters
}) => {
  const {
    threadId,
    rowsPerPage = DEFAULT_GET_ALL_LIMIT,
    sort,
    filters,
  } = params
  const filterBy: Record<string, string[]> = {}
  const sortBy = convertSortToApiFormat(
    sort || { column: null, direction: null },
    '-created_at'
  )
  filterBy.thread_id = [threadId]

  return useInfiniteQuery({
    queryKey: ANNOTATIONS_COMMENTS([
      'comments-list',
      threadId,
      rowsPerPage,
      sortBy,
      JSON.stringify(filters || {}),
    ]),
    queryFn: ({ pageParam = DEFAULT_PAGE }) =>
      annotationApi.getCommentsList({
        filter_by: filterBy,
        sort_by: sortBy,
        limit: rowsPerPage,
        offset: pageParam * rowsPerPage,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const hasMore = lastPage?.pagination?.has_next ?? false
      return hasMore ? allPages.length : undefined
    },
    initialPageParam: DEFAULT_PAGE,
    enabled: !!threadId,
    staleTime: COMMENTS_STALE_TIME,
    gcTime: COMMENTS_GC_TIME,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  })
}

/**
 * Hook to check if a thread is synced
 * @param params - Parameters for the query
 * @returns React Query query hook
 */
export const useCheckThreadSync = <
  TData = Awaited<ReturnType<typeof annotationApi.checkThreadSync>>,
  TError = ErrorType<unknown>,
>(
  threadId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof annotationApi.checkThreadSync>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof annotationApi.checkThreadSync>>,
          TError,
          Awaited<ReturnType<typeof annotationApi.checkThreadSync>>
        >,
        'initialData'
      >
  }
) => {
  const { query: queryOptions } = options ?? {}

  return useQuery({
    queryKey: ANNOTATIONS_THREAD_COMMENTS(['sync', threadId]),
    queryFn: () => annotationApi.checkThreadSync(threadId),
    enabled: !!threadId,
    refetchInterval: SYNC_REFETCH_TIME,
    ...queryOptions,
  })
}

/**
 * Hook to retry a thread sync
 * @param params - Parameters for the query
 * @returns React Query query hook
 */
export const useRetryThreadSync = <
  TError = ErrorType<unknown | null>, // NOSONAR
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof annotationApi.retryThreadSync>>,
    TError,
    { threadId: string },
    TContext
  >
  request?: { threadId: string }
}) => {
  const { mutation, request } = options || {}
  const { threadId = '' } = request || {}
  return useMutation({
    mutationFn: () => annotationApi.retryThreadSync(threadId),
    ...mutation,
  })
}
